import React from 'react'
import { createPortal } from 'react-dom'

/**
 * Общий поповер в стиле проекта (как у RequiredFieldPopover).
 * placement: 'top' — над триггером; 'bottom' — под; 'right' — справа (стрелка слева от окна).
 * Для placement 'right' при нехватке места справа автоматически показывается слева от триггера.
 * portal — рендер в document.body (fixed), чтобы не обрезался overflow у таблиц/скролла.
 * Для click-режима при visible можно рендерить backdrop с onClose.
 */
export function SigurPopover({
  visible,
  onClose,
  placement = 'top',
  portal = false,
  trigger,
  children,
  className = '',
}) {
  const wrapperRef = React.useRef(null)
  const popoverRef = React.useRef(null)
  const [flipToLeft, setFlipToLeft] = React.useState(false)
  const [fixedPos, setFixedPos] = React.useState(null)

  const effectivePlacement =
    placement === 'right' ? (flipToLeft ? 'left' : 'right') : placement

  React.useLayoutEffect(() => {
    if (!visible || placement !== 'right' || portal) {
      setFlipToLeft(false)
      return
    }
    const measure = () => {
      const wrap = wrapperRef.current
      const pop = popoverRef.current
      if (!wrap) return
      const vw = window.innerWidth || document.documentElement.clientWidth
      const edge = 12
      const gap = 10
      const rect = wrap.getBoundingClientRect()
      const popW = pop ? pop.getBoundingClientRect().width : 220
      const overflowRight = rect.right + gap + popW > vw - edge
      setFlipToLeft(overflowRight)
    }
    measure()
    queueMicrotask(measure)
  }, [visible, placement, portal, children])

  React.useLayoutEffect(() => {
    if (!visible || !portal) {
      setFixedPos(null)
      return undefined
    }

    const position = () => {
      const wrap = wrapperRef.current
      const pop = popoverRef.current
      if (!wrap) return

      const anchor = wrap.getBoundingClientRect()
      const popW = pop?.offsetWidth || 200
      const popH = pop?.offsetHeight || 48
      const gap = 8
      const margin = 8
      let top = 0
      let left = 0

      if (effectivePlacement === 'top') {
        top = anchor.top - popH - gap
        left = anchor.left + anchor.width / 2 - popW / 2
      } else if (effectivePlacement === 'bottom') {
        top = anchor.bottom + gap
        left = anchor.left + anchor.width / 2 - popW / 2
      } else if (effectivePlacement === 'right') {
        top = anchor.top + anchor.height / 2 - popH / 2
        left = anchor.right + gap
      } else {
        top = anchor.top + anchor.height / 2 - popH / 2
        left = anchor.left - popW - gap
      }

      left = Math.max(margin, Math.min(left, window.innerWidth - popW - margin))
      top = Math.max(margin, Math.min(top, window.innerHeight - popH - margin))
      setFixedPos({ top, left })
    }

    position()
    const raf = requestAnimationFrame(position)
    return () => cancelAnimationFrame(raf)
  }, [visible, portal, effectivePlacement, children])

  const popoverNode = visible ? (
    <div
      ref={popoverRef}
      className={`sigur-popover sigur-popover--${effectivePlacement}${portal ? ' sigur-popover--portal' : ''}`}
      style={
        portal && fixedPos
          ? { top: fixedPos.top, left: fixedPos.left }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      <span className={`sigur-popover__arrow sigur-popover__arrow--${effectivePlacement}`} />
      <span className="sigur-popover__content">{children}</span>
    </div>
  ) : null

  return (
    <span ref={wrapperRef} className={`sigur-popover-wrapper ${className}`.trim()}>
      {trigger}
      {visible && onClose && (
        <div
          className="sigur-popover-backdrop"
          aria-hidden
          onClick={onClose}
        />
      )}
      {portal && popoverNode
        ? createPortal(popoverNode, document.body)
        : popoverNode}
    </span>
  )
}
